// src/pages/AdminUsers.js
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate }                  from 'react-router-dom';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc
} from 'firebase/firestore';
import { db, callXeroFunction }          from '../firebase.js';
import { Container, Button, Form, Row, Col, Card, Modal, Badge, Alert, Spinner, Dropdown, ButtonGroup } from 'react-bootstrap';
import { useAuth }                      from '../context/AuthContext.js';
import { useLanguage }                  from '../context/LanguageContext.js';
import { t }                            from '../utils/translations.js';
import { exportClientsCSV, exportEmployeesCSV, exportTeamScheduleCSV, exportJobsCSV } from '../utils/csvExport.js';
import LoadingSpinner from '../components/common/LoadingSpinner.js';
import './AdminUsers.css';

const AVATAR_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#6366f1'];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
};

const getAvatarColor = (name) => {
  const idx = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[idx % AVATAR_COLORS.length];
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();

  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [newName, setNewName]       = useState('');
  const [newEmail, setNewEmail]     = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // For edit modal
  const [editUser, setEditUser]               = useState(null);
  const [editName, setEditName]               = useState('');
  const [editEmail, setEditEmail]             = useState('');
  const [editIsAdmin, setEditIsAdmin]         = useState(false);
  const [editIsClientManager, setEditIsClientManager] = useState(false);
  const [editHourlyRate, setEditHourlyRate]   = useState('');
  const [editDeductionRate, setEditDeductionRate] = useState('');
  const [savingEdit, setSavingEdit]           = useState(false);
  const [searchTerm, setSearchTerm]           = useState('');

  // Custom members (external team members)
  const [customMembers, setCustomMembers] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(false);

  // CSV Export
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [loadingExport, setLoadingExport] = useState(false);

  // Filtra e ordena usuários
  const filteredUsers = useMemo(() => {
    let result = [...users];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(u =>
        (u.displayName || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term)
      );
    }
    return result.sort((a, b) => {
      const nameA = (a.displayName || a.email).toLowerCase();
      const nameB = (b.displayName || b.email).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users, searchTerm]);

  // Load users (only if admin)
  useEffect(() => {
    if (!user?.isAdmin) return;

    (async () => {
      const snap = await getDocs(collection(db, 'users'));
      setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
      setLoading(false);
    })();

    // Buscar custom members
    (async () => {
      setLoadingCustom(true);
      try {
        const result = await callXeroFunction('getCustomMembers', {}, { method: 'GET' });
        setCustomMembers(result.members || []);
      } catch (err) {
        console.error('Error fetching custom members:', err);
      }
      setLoadingCustom(false);
    })();
  }, [user?.isAdmin]);

  // Security: Block non-admins completely
  if (!user?.isAdmin) {
    return (
      <Container className="py-4">
        <div className="alert alert-danger">
          {language === 'en'
            ? '⛔ Access denied. Only administrators can access this page.'
            : '⛔ Acesso negado. Apenas administradores podem acessar esta página.'}
        </div>
      </Container>
    );
  }

  const reload = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, 'users'));
    setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
    setLoading(false);
  };

  // Add user (ADMIN ONLY)
  const handleAddUser = async e => {
    e.preventDefault();

    // Security check
    if (!user?.isAdmin) {
      alert(language === 'en' ? 'Only admins can add users' : 'Apenas admins podem adicionar usuários');
      return;
    }

    if (!newName.trim() || !newEmail.trim()) {
      return alert(t(language, 'fillNameEmail'));
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'users'), {
        displayName: newName.trim(),
        email: newEmail.trim(),
        isAdmin: newIsAdmin,
        isClientManager: false
      });
      setNewName('');
      setNewEmail('');
      setNewIsAdmin(false);
      await reload();
    } catch (err) {
      console.error(err);
      alert(t(language, 'errorAddingUser'));
    }
    setSubmitting(false);
  };

  // Toggle admin (ADMIN ONLY)
  const handleToggleAdmin = async u => {
    // Security check
    if (!user?.isAdmin) {
      alert(language === 'en' ? 'Only admins can change permissions' : 'Apenas admins podem alterar permissões');
      return;
    }

    try {
      const ref = doc(db, 'users', u.uid);
      await updateDoc(ref, { isAdmin: !u.isAdmin });
      await reload();
    } catch (err) {
      console.error(err);
      alert(t(language, 'errorUpdatingPermission'));
    }
  };

  // Toggle client manager (ADMIN ONLY)
  const handleToggleClientManager = async u => {
    // Security check
    if (!user?.isAdmin) {
      alert(language === 'en' ? 'Only admins can change permissions' : 'Apenas admins podem alterar permissões');
      return;
    }

    try {
      const ref = doc(db, 'users', u.uid);
      await updateDoc(ref, { isClientManager: !u.isClientManager });
      await reload();
    } catch (err) {
      console.error(err);
      alert(t(language, 'errorUpdatingPermission'));
    }
  };

  // Delete user (ADMIN ONLY)
  const handleDelete = async u => {
    // Security check
    if (!user?.isAdmin) {
      alert(language === 'en' ? 'Only admins can delete users' : 'Apenas admins podem deletar usuários');
      return;
    }

    if (!window.confirm(`${t(language, 'deleteUserConfirm')} "${u.displayName}"?`)) return;

    try {
      console.log(`🗑️ Deletando usuário: ${u.displayName} (${u.uid})`);

      // 1. Delete user from users collection
      await deleteDoc(doc(db, 'users', u.uid));
      console.log('✅ Usuário deletado da collection users');

      // 2. Clean up references in weeklyTeams
      const weeklyTeamsSnap = await getDocs(collection(db, 'weeklyTeams'));
      if (!weeklyTeamsSnap.empty) {
        const teamDoc = weeklyTeamsSnap.docs[0];
        const weeklyTeamsData = teamDoc.data();
        let hasChanges = false;

        // Create a cleaned version of weeklyTeams
        const cleanedWeeklyTeams = {};

        // Process each team (team1, team2, team3)
        ['team1', 'team2', 'team3'].forEach(teamKey => {
          if (weeklyTeamsData[teamKey]) {
            cleanedWeeklyTeams[teamKey] = {};

            // Process each day of the week
            ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].forEach(day => {
              const teamForDay = weeklyTeamsData[teamKey][day] || [];

              // Filter out the deleted user's UID
              const cleanedTeam = teamForDay.filter(uid => {
                // Keep custom members (they start with "custom:")
                if (uid.startsWith('custom:')) return true;
                // Remove the deleted user's UID
                if (uid === u.uid) {
                  console.log(`🧹 Removendo ${u.displayName} de ${teamKey}.${day}`);
                  hasChanges = true;
                  return false;
                }
                return true;
              });

              cleanedWeeklyTeams[teamKey][day] = cleanedTeam;
            });
          } else {
            // Initialize empty structure if team doesn't exist
            cleanedWeeklyTeams[teamKey] = {
              Monday: [], Tuesday: [], Wednesday: [], Thursday: [],
              Friday: [], Saturday: [], Sunday: []
            };
          }
        });

        // Save cleaned data back to Firebase if there were changes
        if (hasChanges) {
          await updateDoc(doc(db, 'weeklyTeams', teamDoc.id), cleanedWeeklyTeams);
          console.log('✅ weeklyTeams limpo e salvo');
        } else {
          console.log('ℹ️ Nenhuma referência encontrada em weeklyTeams');
        }
      }

      // 3. Clean up references in dailyTeamOverrides
      const dailyOverridesSnap = await getDocs(collection(db, 'dailyTeamOverrides'));
      let overrideChanges = 0;

      for (const overrideDoc of dailyOverridesSnap.docs) {
        const dateStr = overrideDoc.id;
        const data = overrideDoc.data();
        let hasChanges = false;
        const cleanedData = {};

        // Process each team in this daily override
        ['team1', 'team2', 'team3'].forEach(teamKey => {
          if (data[teamKey] && Array.isArray(data[teamKey])) {
            // Filter out the deleted user's UID
            const cleanedTeam = data[teamKey].filter(uid => {
              // Keep custom members
              if (uid.startsWith('custom:')) return true;
              // Remove the deleted user's UID
              if (uid === u.uid) {
                console.log(`🧹 Removendo ${u.displayName} de override diário ${dateStr}.${teamKey}`);
                hasChanges = true;
                return false;
              }
              return true;
            });

            cleanedData[teamKey] = cleanedTeam;
          } else {
            cleanedData[teamKey] = data[teamKey];
          }
        });

        // Save cleaned data back to Firebase if there were changes
        if (hasChanges) {
          await updateDoc(doc(db, 'dailyTeamOverrides', dateStr), cleanedData);
          overrideChanges++;
        }
      }

      if (overrideChanges > 0) {
        console.log(`✅ ${overrideChanges} override(s) diário(s) limpo(s)`);
      } else {
        console.log('ℹ️ Nenhuma referência encontrada em dailyTeamOverrides');
      }

      // 4. Reload users list
      await reload();

      console.log('✅ Limpeza completa! Os cálculos serão atualizados automaticamente.');
      alert(
        language === 'en'
          ? `User "${u.displayName}" deleted and all references cleaned up!`
          : `Usuário "${u.displayName}" deletado e todas as referências limpas!`
      );

    } catch (err) {
      console.error('❌ Erro ao deletar usuário:', err);
      alert(t(language, 'errorDeletingUser'));
    }
  };

  // Open edit modal
  const openEdit = u => {
    setEditUser(u);
    setEditName(u.displayName || '');
    setEditEmail(u.email || '');
    setEditIsAdmin(!!u.isAdmin);
    setEditIsClientManager(!!u.isClientManager);
    setEditHourlyRate(u.hourlyRate != null ? String(u.hourlyRate) : '');
    setEditDeductionRate(u.deductionRate != null ? String(parseFloat(u.deductionRate) * 100) : '');
  };

  // Save edit (ADMIN ONLY)
  const handleSaveEdit = async () => {
    // Security check
    if (!user?.isAdmin) {
      alert(language === 'en' ? 'Only admins can edit users' : 'Apenas admins podem editar usuários');
      return;
    }

    if (!editName.trim() || !editEmail.trim()) {
      return alert(t(language, 'fillNameEmail'));
    }
    setSavingEdit(true);
    try {
      const refDoc = doc(db, 'users', editUser.uid);
      const updateData = {
        displayName: editName.trim(),
        email: editEmail.trim(),
        isAdmin: editIsAdmin,
        isClientManager: editIsClientManager
      };
      // Salary fields (admin only)
      if (editHourlyRate.trim()) {
        updateData.hourlyRate = parseFloat(editHourlyRate) || 0;
      } else {
        updateData.hourlyRate = null;
      }
      if (editDeductionRate.trim()) {
        updateData.deductionRate = (parseFloat(editDeductionRate) || 0) / 100;
      } else {
        updateData.deductionRate = null;
      }
      await updateDoc(refDoc, updateData);
      setEditUser(null);
      await reload();
    } catch (err) {
      console.error(err);
      alert(t(language, 'errorAddingUser'));
    }
    setSavingEdit(false);
  };

  // Export data - fetch once, then download individual or all
  const fetchExportData = async () => {
    if (exportData) return exportData;
    setLoadingExport(true);
    try {
      const data = await callXeroFunction('exportAllData');
      setExportData(data);
      return data;
    } catch (err) {
      console.error('Export error:', err);
      setExportResult({ success: false, error: err.message });
      return null;
    } finally {
      setLoadingExport(false);
    }
  };

  const handleExportSingle = async (type) => {
    setExporting(type);
    setExportResult(null);
    const data = await fetchExportData();
    if (!data) { setExporting(false); return; }
    let count = 0;
    if (type === 'clients') count = exportClientsCSV(data.clients || []);
    if (type === 'employees') count = exportEmployeesCSV(data.employees || [], data.customMembers || []);
    if (type === 'schedule') count = exportTeamScheduleCSV(data.teamSchedule, data.employees || [], data.customMembers || []);
    if (type === 'jobs') count = exportJobsCSV(data.jobs || []);
    setExportResult({ success: true, message: `${count} ${type} exported` });
    setExporting(false);
  };

  const handleExportAll = async () => {
    setExporting('all');
    setExportResult(null);
    const data = await fetchExportData();
    if (!data) { setExporting(false); return; }
    const clientCount = exportClientsCSV(data.clients || []);
    await new Promise(r => setTimeout(r, 1500));
    const empCount = exportEmployeesCSV(data.employees || [], data.customMembers || []);
    await new Promise(r => setTimeout(r, 1500));
    const scheduleCount = exportTeamScheduleCSV(data.teamSchedule, data.employees || [], data.customMembers || []);
    await new Promise(r => setTimeout(r, 1500));
    const jobCount = exportJobsCSV(data.jobs || []);
    setExportResult({
      success: true,
      message: `${clientCount} clients, ${empCount} employees, ${scheduleCount} schedule, ${jobCount} jobs (4 CSV files)`,
    });
    setExporting(false);
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>{t(language, 'adminPanel')}</h1>
          <small className="text-muted">
            {filteredUsers.length} {t(language, 'users')}
          </small>
        </Col>
        {user?.isAdmin && <Col xs="auto">
          <Dropdown as={ButtonGroup}>
            <Button
              variant="outline-success"
              onClick={handleExportAll}
              disabled={!!exporting}
            >
              {exporting ? (
                <><Spinner animation="border" size="sm" className="me-2" />Exporting...</>
              ) : (
                'Export CSV'
              )}
            </Button>
            <Dropdown.Toggle split variant="outline-success" disabled={!!exporting} />
            <Dropdown.Menu align="end">
              <Dropdown.Header>Download individual</Dropdown.Header>
              <Dropdown.Item onClick={() => handleExportSingle('clients')}>Clients</Dropdown.Item>
              <Dropdown.Item onClick={() => handleExportSingle('employees')}>Employees</Dropdown.Item>
              <Dropdown.Item onClick={() => handleExportSingle('schedule')}>Team Schedule</Dropdown.Item>
              <Dropdown.Item onClick={() => handleExportSingle('jobs')}>Jobs (Calendar)</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleExportAll}>All (4 files)</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>}
      </Row>

      {exportResult && (
        <Alert
          variant={exportResult.success ? 'success' : 'danger'}
          dismissible
          onClose={() => setExportResult(null)}
          className="mb-3"
        >
          {exportResult.success
            ? `Exported: ${exportResult.message}`
            : `Error: ${exportResult.error}`
          }
        </Alert>
      )}

      {/* Add User */}
      <Card className="mb-5 p-4">
        <Card.Header className="bg-transparent border-0 pb-0 mb-3">
          <h5 className="mb-0">➕ {t(language, 'addUser')}</h5>
        </Card.Header>
        <Form onSubmit={handleAddUser}>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label>{t(language, 'userName')}</Form.Label>
              <Form.Control
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder={t(language, 'displayName')}
              />
            </Col>
            <Col md={4}>
              <Form.Label>{t(language, 'email')}</Form.Label>
              <Form.Control
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </Col>
            <Col md={2}>
              <Form.Check
                type="checkbox"
                label={t(language, 'isAdmin')}
                checked={newIsAdmin}
                onChange={e => setNewIsAdmin(e.target.checked)}
              />
            </Col>
            <Col md={2}>
              <Button type="submit" disabled={submitting} className="w-100">
                {submitting ? t(language, 'addingUser') : t(language, 'addUser')}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Users List */}
      <Card className="shadow-sm border-0 overflow-hidden">
        <div className="px-3 py-3 bg-light border-bottom">
          <Form.Control
            type="search"
            placeholder={language === 'en' ? 'Search users...' : 'Buscar usuários...'}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="border-0 bg-white shadow-sm"
            style={{ borderRadius: '10px' }}
          />
        </div>

        <div className="list-group list-group-flush">
          {filteredUsers.map(u => (
            <div
              key={u.uid}
              className="list-group-item user-list-item px-3 py-3"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                // Não navegar se clicou num botão de ação
                if (e.target.closest('.user-actions')) return;
                navigate(`/admin/users/${u.uid}/profile`);
              }}
            >
              <div className="d-flex align-items-center gap-3">
                {/* Avatar */}
                <div
                  className="user-avatar"
                  style={{ backgroundColor: getAvatarColor(u.displayName || u.email) }}
                  title={t(language, 'viewProfile')}
                >
                  {getInitials(u.displayName || u.email)}
                </div>

                {/* Info + Badges */}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-semibold text-truncate" style={{ fontSize: '1.05rem' }}>{u.displayName || u.email}</span>
                    {u.isAdmin && <Badge bg="primary" pill style={{ fontSize: '0.7rem' }}>Admin</Badge>}
                    {u.isClientManager && <Badge bg="info" pill style={{ fontSize: '0.7rem' }}>Gerenciador</Badge>}
                  </div>
                  <small className="text-muted text-truncate d-block">{u.email}</small>
                </div>

                {/* Actions */}
                <div className="user-actions d-flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="outline-success" onClick={() => navigate(`/admin/users/${u.uid}/colaborador/${encodeURIComponent(u.displayName || u.email)}`)} title={language === 'en' ? 'Weekly Report' : 'Fechamento Semanal'} className="user-action-btn">
                    📅
                  </Button>
                  <Button size="sm" variant="outline-primary" onClick={() => navigate(`/admin/users/${u.uid}/profile`)} title={t(language, 'viewProfile')} className="user-action-btn">
                    📊
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => openEdit(u)} title={t(language, 'edit')} className="user-action-btn">
                    ✏️
                  </Button>
                  <Button size="sm" variant={u.isAdmin ? 'outline-warning' : 'outline-success'} onClick={() => handleToggleAdmin(u)} title={u.isAdmin ? 'Remover Admin' : 'Tornar Admin'} className="user-action-btn">
                    {u.isAdmin ? '👤' : '👑'}
                  </Button>
                  <Button size="sm" variant={u.isClientManager ? 'outline-info' : 'outline-secondary'} onClick={() => handleToggleClientManager(u)} title={u.isClientManager ? 'Desativar Gerenciador' : 'Ativar Gerenciador'} className="user-action-btn">
                    📋
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(u)} title="Deletar" className="user-action-btn">
                    🗑️
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredUsers.length === 0 && searchTerm && (
          <div className="text-center py-4 text-muted">
            {language === 'en' ? 'No users found' : 'Nenhum usuário encontrado'}
          </div>
        )}

        <Card.Footer className="bg-light text-muted small text-center py-2">
          {filteredUsers.length} usuário{filteredUsers.length !== 1 ? 's' : ''}
        </Card.Footer>
      </Card>

      {/* Custom Members (External) */}
      {customMembers.length > 0 && (
        <Card className="shadow-sm border-0 overflow-hidden mt-4">
          <Card.Header className="bg-transparent border-bottom">
            <h5 className="mb-0">
              {language === 'en' ? 'External Members' : 'Membros Externos'}
              <Badge bg="warning" text="dark" pill className="ms-2" style={{ fontSize: '0.75rem' }}>
                {customMembers.length}
              </Badge>
            </h5>
            <small className="text-muted">
              {language === 'en'
                ? 'Team members without a system account'
                : 'Membros do time sem conta no sistema'}
            </small>
          </Card.Header>

          <div className="list-group list-group-flush">
            {customMembers.map(m => {
              const slug = encodeURIComponent(m.name);
              return (
                <div
                  key={m.id}
                  className="list-group-item user-list-item px-3 py-3"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/users/${encodeURIComponent(m.id)}/colaborador/${slug}`)}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="user-avatar"
                      style={{ backgroundColor: getAvatarColor(m.name) }}
                    >
                      {getInitials(m.name)}
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-semibold" style={{ fontSize: '1.05rem' }}>{m.name}</span>
                        <Badge bg="warning" text="dark" pill style={{ fontSize: '0.7rem' }}>
                          {language === 'en' ? 'External' : 'Externo'}
                        </Badge>
                        {m.teams.map(team => (
                          <Badge key={team} bg="secondary" pill style={{ fontSize: '0.65rem' }}>
                            {team}
                          </Badge>
                        ))}
                      </div>
                      {m.days.length > 0 && (
                        <small className="text-muted">
                          {m.days.map(d => d.slice(0, 3)).join(', ')}
                        </small>
                      )}
                    </div>
                    <div className="text-muted" style={{ fontSize: '1.2rem' }}>
                      ›
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {loadingCustom && (
        <div className="text-center py-3 text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          {language === 'en' ? 'Loading external members...' : 'Carregando membros externos...'}
        </div>
      )}

      {/* Edit User Modal */}
      <Modal show={!!editUser} onHide={() => setEditUser(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{t(language, 'editUser')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>{t(language, 'userName')}</Form.Label>
              <Form.Control
                value={editName}
                onChange={e => setEditName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>{t(language, 'email')}</Form.Label>
              <Form.Control
                type="email"
                value={editEmail}
                onChange={e => setEditEmail(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                label={t(language, 'isAdmin')}
                checked={editIsAdmin}
                onChange={e => setEditIsAdmin(e.target.checked)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                label="📋 Gerenciador de Clientes"
                checked={editIsClientManager}
                onChange={e => setEditIsClientManager(e.target.checked)}
              />
            </Form.Group>
            <hr />
            <small className="text-muted fw-semibold d-block mb-2">{t(language, 'salarySettings')}</small>
            <Row>
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>{t(language, 'hourlyRate')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.5"
                    min="0"
                    value={editHourlyRate}
                    onChange={e => setEditHourlyRate(e.target.value)}
                    placeholder="28"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>{t(language, 'deductionRate')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={editDeductionRate}
                    onChange={e => setEditDeductionRate(e.target.value)}
                    placeholder="16.5"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditUser(null)}>
            {t(language, 'cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEdit}
            disabled={savingEdit}
          >
            {savingEdit ? t(language, 'saving') : t(language, 'save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
